/** Chave do zoom escolhido pelo operador (fica só neste equipamento). */
export const SCREEN_ZOOM_KEY = "@screen-zoom";

export const DEFAULT_SCREEN_ZOOM = 1;

/** Opções do seletor na área do operador: 50% a 150%. */
export const SCREEN_ZOOM_OPTIONS = [0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5];

const isValidZoom = (value: number) => SCREEN_ZOOM_OPTIONS.includes(value);

export const getStoredScreenZoom = (storage: Pick<Storage, "getItem"> = localStorage): number => {
  try {
    const value = Number(storage.getItem(SCREEN_ZOOM_KEY));
    return isValidZoom(value) ? value : DEFAULT_SCREEN_ZOOM;
  } catch (error) {
    return DEFAULT_SCREEN_ZOOM;
  }
};

/**
 * Aplica o zoom na página inteira. O `zoom` do CSS também multiplica `vh`/`vw`,
 * então `--vh`/`--vw` compensam: os estilos usam `calc(N * var(--vh, 1vh))` e
 * continuam ocupando a tela toda.
 */
export const applyScreenZoom = (zoom: number, root: HTMLElement = document.documentElement) => {
  const value = isValidZoom(zoom) ? zoom : DEFAULT_SCREEN_ZOOM;
  if (value === DEFAULT_SCREEN_ZOOM) {
    root.style.removeProperty("zoom");
    root.style.removeProperty("--vh");
    root.style.removeProperty("--vw");
    return;
  }
  root.style.setProperty("zoom", String(value));
  root.style.setProperty("--vh", `calc(1vh / ${value})`);
  root.style.setProperty("--vw", `calc(1vw / ${value})`);
};

const ZOOM_PORTAL_ID = "zoom-portal";

/**
 * Chromium 128+ (WebView Android, Chrome) devolve `getBoundingClientRect` em px
 * da tela, mas `top/left` de quem está dentro do zoom continuam multiplicados por
 * ele. O Popover do MUI mistura os dois e abre deslocado. O Chromium 83 da casca
 * Windows mede tudo em px do zoom e não precisa de nada.
 */
const rectsIgnoreZoom = (zoom: number) => {
  const probe = document.createElement("div");
  probe.style.cssText = "position:absolute;visibility:hidden;width:100px;height:100px";
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return Math.abs(height - 100 * zoom) < 1;
};

/**
 * Onde o MUI monta menus e popovers. Com zoom num Chromium novo, é um contêiner
 * que desfaz o zoom (`1/zoom`): o menu aparece em 100%, mas alinhado ao campo.
 */
export const getZoomPortal = (): HTMLElement => {
  const zoom = getStoredScreenZoom();
  const existing = document.getElementById(ZOOM_PORTAL_ID);
  if (zoom === DEFAULT_SCREEN_ZOOM || !rectsIgnoreZoom(zoom)) {
    existing?.remove();
    return document.body;
  }
  const portal = existing ?? document.createElement("div");
  portal.id = ZOOM_PORTAL_ID;
  portal.style.setProperty("zoom", String(1 / zoom));
  if (!existing) document.body.appendChild(portal);
  return portal;
};

export const saveScreenZoom = (
  zoom: number,
  storage: Pick<Storage, "setItem"> = localStorage,
  root?: HTMLElement
) => {
  const value = isValidZoom(zoom) ? zoom : DEFAULT_SCREEN_ZOOM;
  try {
    storage.setItem(SCREEN_ZOOM_KEY, String(value));
  } catch (error) {
    console.error("Não foi possível salvar o zoom da tela:", error);
  }
  applyScreenZoom(value, root);
  // ScreenStore e quem mede a janela recalculam com o novo tamanho útil.
  window.dispatchEvent(new Event("resize"));
  return value;
};
